import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type { TaxDocumentDetails } from "../types";

export interface UseTaxDocumentResult {
  document: TaxDocumentDetails | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useTaxDocument(documentId: string | undefined): UseTaxDocumentResult {
  const [document, setDocument] = useState<TaxDocumentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocument = useCallback(async () => {
    if (!documentId) {
      setDocument(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch.get<{ document: TaxDocumentDetails }>(
        `/api/tax-documents/${documentId}`
      );
      setDocument(response.document);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch document"));
    } finally {
      setIsLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  return {
    document,
    isLoading,
    error,
    refetch: fetchDocument,
  };
}
